import { useState, useEffect } from 'react'
import styles from './WorkspaceModal.module.css'
import WorkspaceDeletionProgress from '../WorkspaceDeletionProgress'
import Toast from '../Toast'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function WorkspaceModal({ isOpen, onClose, workspaces, onWorkspaceCreate, onWorkspaceUpdate, onWorkspaceDelete, currentUser }) {
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [editingWorkspace, setEditingWorkspace] = useState(null)
    const [deletingWorkspace, setDeletingWorkspace] = useState(null)
    const [showProgress, setShowProgress] = useState(false)
    const [deletionCompleted, setDeletionCompleted] = useState(false)
    const [showSuccessToast, setShowSuccessToast] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [formData, setFormData] = useState({
        name: '',
        plan: 'free',
        supported_currencies: ['USD'],
        currency_format: '$#,##0.00',
        report_filename_format: 'Kadle $client $fromDate - $toDate',
        approval_enabled: false,
        send_invitation_emails: true,
        logo: null
    })

    // Add escape key functionality - allow escape unless in deletion progress
    useEscapeKey(isOpen, onClose, !showProgress)

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setShowCreateForm(false)
            setEditingWorkspace(null)
            setDeletingWorkspace(null)
            setShowProgress(false)
            setDeletionCompleted(false)
            setShowSuccessToast(false)
            setSuccessMessage('')
            setFormData({ 
                name: '',
                plan: 'free',
                supported_currencies: ['USD'],
                currency_format: '$#,##0.00',
                report_filename_format: 'Kadle $client $fromDate - $toDate',
                approval_enabled: false,
                send_invitation_emails: true,
                logo: null
            })
        }
    }, [isOpen])

    const handleCreateWorkspace = async (e) => {
        e.preventDefault()
        if (!formData.name.trim()) return

        try {
            await onWorkspaceCreate(formData)
            setShowCreateForm(false)
            setFormData({ 
                name: '',
                plan: 'free',
                supported_currencies: ['USD'],
                currency_format: '$#,##0.00',
                report_filename_format: 'Kadle $client $fromDate - $toDate',
                approval_enabled: false,
                send_invitation_emails: true,
                logo: null
            })
        } catch (error) {
            console.error('Failed to create workspace:', error)
        }
    }

    const handleUpdateWorkspace = async (e) => {
        e.preventDefault()
        if (!formData.name.trim() || !editingWorkspace) return

        try {
            await onWorkspaceUpdate(editingWorkspace.id, formData)
            setEditingWorkspace(null)
            setFormData({ 
                name: '',
                plan: 'free',
                supported_currencies: ['USD'],
                currency_format: '$#,##0.00',
                report_filename_format: 'Kadle $client $fromDate - $toDate',
                approval_enabled: false,
                send_invitation_emails: true,
                logo: null
            })
        } catch (error) {
            console.error('Failed to update workspace:', error)
        }
    }

    const handleEditClick = (workspace) => {
        setEditingWorkspace(workspace)
        setFormData({
            name: workspace.name,
            plan: workspace.plan || 'free',
            supported_currencies: workspace.supported_currencies || ['USD'],
            currency_format: workspace.currency_format || '$#,##0.00',
            report_filename_format: workspace.report_filename_format || 'Kadle $client $fromDate - $toDate',
            approval_enabled: workspace.approval_enabled || false,
            send_invitation_emails: workspace.send_invitation_emails !== undefined ? workspace.send_invitation_emails : true,
            logo: null // Don't prefill existing logo, user needs to upload new one if they want to change it
        })
        setShowCreateForm(false)
    }

    const handleDeleteClick = (workspace) => {
        // Show the progress component instead of immediate deletion
        setDeletingWorkspace(workspace)
        setShowCreateForm(false)
        setEditingWorkspace(null)
    }

    const handleStartDeletion = async (workspace) => {
        try {
            // Call the delete API which will start the background job
            const response = await onWorkspaceDelete(workspace.id)
            console.log('Workspace deletion started:', response)
            
            // Show progress component
            setShowProgress(true)
        } catch (error) {
            console.error('Failed to start workspace deletion:', error)
            alert(`Failed to start deletion: ${error.message || 'Unknown error'}`)
            setDeletingWorkspace(null)
        }
    }

    const handleDeletionComplete = (data) => {
        console.log('Workspace deletion completed:', data)
        
        // Show success toast notification
        setSuccessMessage(`✅ Workspace "${deletingWorkspace?.name}" has been successfully deleted!`)
        setShowSuccessToast(true)
        
        // Clean up deletion state and close modal 
        setDeletingWorkspace(null)
        setShowProgress(false)
        setDeletionCompleted(false)
        
        // Close the modal after a short delay to let user see the toast
        setTimeout(() => {
            onClose()
        }, 500)
    }

    const handleSuccessClose = () => {
        // This is called when user clicks Close button in success message
        setDeletingWorkspace(null)
        setDeletionCompleted(false)
        onClose() // Close modal and refresh workspaces
    }

    const handleDeletionError = (data) => {
        console.error('Workspace deletion failed:', data)
        alert(`Deletion failed: ${data.message || 'Unknown error'}`)
        setDeletingWorkspace(null)
        setShowProgress(false)
    }

    const handleCancelDeletion = () => {
        setDeletingWorkspace(null)
        setShowProgress(false)
        setDeletionCompleted(false)
    }

    const handleCloseToast = () => {
        setShowSuccessToast(false)
        setSuccessMessage('')
    }

    const handleCancelForm = () => {
        setShowCreateForm(false)
        setEditingWorkspace(null)
        setFormData({
            name: '',
            plan: 'free',
            supported_currencies: ['USD'],
            currency_format: '$#,##0.00',
            report_filename_format: 'Kadle $client $fromDate - $toDate',
            approval_enabled: false,
            send_invitation_emails: true
        })
    }

    if (!isOpen) return null

    return (
        <>
            <div className={styles.modalOverlay}>
                <div className={styles.modalContent}>
                    <div className={styles.modalHeader}>
                        <h2 className={styles.modalTitle}>Manage Workspaces</h2>
                        <button 
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>

                <div className={styles.modalBody}>
                    {/* Workspace Deletion Progress */}
                    {deletingWorkspace && !showProgress && (
                        <div className={styles.deletionSection}>
                            <div className={styles.deletionConfirm}>
                                <h3>Delete Workspace: {deletingWorkspace.name}?</h3>
                                <p>This will permanently delete all workspace data:</p>
                                <ul className={styles.deletionList}>
                                    <li>Projects and tasks</li>
                                    <li>Time logs and worklogs</li>
                                    <li>Clients and groups</li>
                                    <li>Workspace settings and preferences</li>
                                    <li>User access permissions</li>
                                    <li>Saved filters and reports</li>
                                </ul>
                                <p className={styles.warningText}>
                                    <strong>⚠️ This action cannot be undone!</strong> All data will be permanently removed.
                                </p>
                                
                                <div className={styles.confirmActions}>
                                    <button 
                                        onClick={() => handleStartDeletion(deletingWorkspace)}
                                        className={styles.confirmDeleteButton}
                                    >
                                        Yes, Delete Everything
                                    </button>
                                    <button 
                                        onClick={handleCancelDeletion}
                                        className={styles.cancelButton}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Real-time Progress and Success */}
                    {deletingWorkspace && (showProgress || deletionCompleted) && (
                        <WorkspaceDeletionProgress
                            workspace={deletingWorkspace}
                            userId={currentUser?.id}
                            onComplete={deletionCompleted ? handleSuccessClose : handleDeletionComplete}
                            onError={handleDeletionError}
                            onCancel={handleCancelDeletion}
                            isCompleted={deletionCompleted}
                        />
                    )}

                    {/* Create/Edit Form */}
                    {(showCreateForm || editingWorkspace) && !deletingWorkspace && (
                        <div className={styles.formSection}>
                            <form onSubmit={editingWorkspace ? handleUpdateWorkspace : handleCreateWorkspace}>
                                <div className={styles.formHeader}>
                                    <h3>{editingWorkspace ? 'Edit Workspace' : 'Create New Workspace'}</h3>
                                </div>
                                
                                <div className={styles.formGridExpanded}>
                                    <div className={styles.formField}>
                                        <label htmlFor="workspaceName">Workspace Name</label>
                                        <input
                                            id="workspaceName"
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Enter workspace name"
                                            required
                                            className={styles.formInput}
                                        />
                                    </div>

                                    <div className={styles.formField}>
                                        <label htmlFor="workspacePlan">Plan</label>
                                        <select
                                            id="workspacePlan"
                                            value={formData.plan}
                                            onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                                            className={styles.formSelect}
                                        >
                                            <option value="free">Free</option>
                                            <option value="pro">Pro</option>
                                            <option value="premium">Premium</option>
                                            <option value="enterprise">Enterprise</option>
                                        </select>
                                    </div>

                                    <div className={styles.formField}>
                                        <label htmlFor="workspaceLogo">Workspace Logo</label>
                                        <input
                                            id="workspaceLogo"
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] })}
                                            className={styles.formInput}
                                        />
                                        <small className={styles.fieldHelp}>Upload an image file (PNG, JPG, etc.)</small>
                                        {editingWorkspace && editingWorkspace.logo_url && (
                                            <div className={styles.currentLogo}>
                                                <span>Current logo:</span>
                                                <img 
                                                    src={editingWorkspace.logo_url} 
                                                    alt="Current workspace logo" 
                                                    className={styles.logoPreview}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.formField}>
                                        <label htmlFor="supportedCurrencies">Supported Currencies</label>
                                        <select
                                            id="supportedCurrencies"
                                            multiple
                                            value={formData.supported_currencies}
                                            onChange={(e) => {
                                                const values = Array.from(e.target.selectedOptions, option => option.value);
                                                setFormData({ ...formData, supported_currencies: values });
                                            }}
                                            className={styles.formSelectMultiple}
                                            size="4"
                                        >
                                            <option value="USD">USD - US Dollar</option>
                                            <option value="EUR">EUR - Euro</option>
                                            <option value="GBP">GBP - British Pound</option>
                                            <option value="JPY">JPY - Japanese Yen</option>
                                            <option value="CAD">CAD - Canadian Dollar</option>
                                            <option value="AUD">AUD - Australian Dollar</option>
                                            <option value="CHF">CHF - Swiss Franc</option>
                                            <option value="CNY">CNY - Chinese Yuan</option>
                                            <option value="INR">INR - Indian Rupee</option>
                                            <option value="KRW">KRW - South Korean Won</option>
                                        </select>
                                        <small className={styles.fieldHelp}>Hold Ctrl/Cmd to select multiple currencies</small>
                                    </div>

                                    <div className={styles.formField}>
                                        <label htmlFor="currencyFormat">Currency Format</label>
                                        <select
                                            id="currencyFormat"
                                            value={formData.currency_format}
                                            onChange={(e) => setFormData({ ...formData, currency_format: e.target.value })}
                                            className={styles.formSelect}
                                        >
                                            <option value="$#,##0.00">$1,234.56 (US Format)</option>
                                            <option value="€#,##0.00">€1,234.56 (Euro Format)</option>
                                            <option value="£#,##0.00">£1,234.56 (UK Format)</option>
                                            <option value="#,##0.00 ¥">1,234.56 ¥ (Yen Format)</option>
                                            <option value="#.##0,00 €">1.234,56 € (European Format)</option>
                                        </select>
                                    </div>

                                    <div className={styles.formField}>
                                        <label htmlFor="reportFilenameFormat">Report Filename Format</label>
                                        <input
                                            id="reportFilenameFormat"
                                            type="text"
                                            value={formData.report_filename_format}
                                            onChange={(e) => setFormData({ ...formData, report_filename_format: e.target.value })}
                                            placeholder="Kadle $client $fromDate - $toDate"
                                            className={styles.formInput}
                                        />
                                        <small className={styles.fieldHelp}>Available variables: $workspace, $client, $fromDate, $toDate, $fromTime, $toTime</small>
                                    </div>

                                </div>

                                <div className={styles.checkboxSection}>
                                    <div className={styles.formField}>
                                        <div className={styles.checkboxGroup}>
                                            <label htmlFor="approvalEnabled" className={styles.checkboxLabel}>
                                                <input
                                                    id="approvalEnabled"
                                                    type="checkbox"
                                                    checked={formData.approval_enabled}
                                                    onChange={(e) => setFormData({ ...formData, approval_enabled: e.target.checked })}
                                                    className={styles.checkbox}
                                                />
                                                Enable Approval Workflow
                                            </label>
                                            <small className={styles.fieldHelp}>Require approval for time entries</small>
                                        </div>
                                    </div>

                                    <div className={styles.formField}>
                                        <div className={styles.checkboxGroup}>
                                            <label htmlFor="sendInvitationEmails" className={styles.checkboxLabel}>
                                                <input
                                                    id="sendInvitationEmails"
                                                    type="checkbox"
                                                    checked={formData.send_invitation_emails}
                                                    onChange={(e) => setFormData({ ...formData, send_invitation_emails: e.target.checked })}
                                                    className={styles.checkbox}
                                                />
                                                Send Invitation Emails
                                            </label>
                                            <small className={styles.fieldHelp}>Automatically send email invitations when adding new members to this workspace</small>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.formActions}>
                                    <button
                                        type="button"
                                        onClick={handleCancelForm}
                                        className={styles.cancelButton}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.submitButton}
                                    >
                                        {editingWorkspace ? 'Update Workspace' : 'Create Workspace'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Workspaces Table */}
                    {!showCreateForm && !editingWorkspace && !deletingWorkspace && (
                    <div className={styles.tableSection}>
                        <div className={styles.tableHeader}>
                            <h3>Your Workspaces</h3>
                            {!showCreateForm && !editingWorkspace && (
                                <button
                                    onClick={() => setShowCreateForm(true)}
                                    className={styles.createButton}
                                >
                                    + Create Workspace
                                </button>
                            )}
                        </div>

                        <div className={styles.tableContainer}>
                            <table className={styles.workspaceTable}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Plan</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {workspaces && workspaces.map(workspace => (
                                        <tr key={workspace.id}>
                                            <td className={styles.workspaceNameCell}>
                                                <span className={styles.workspaceIcon}>🏢</span>
                                                <span className={styles.workspaceName}>{workspace.name}</span>
                                            </td>
                                            <td>
                                                <span className={`${styles.planBadge} ${styles[`plan-${workspace.plan || 'free'}`]}`}>
                                                    {(workspace.plan || 'free').toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${styles[`status-${workspace.status || 'active'}`]}`}>
                                                    {(workspace.status || 'active').charAt(0).toUpperCase() + (workspace.status || 'active').slice(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.actionButtons}>
                                                    <button
                                                        onClick={() => handleEditClick(workspace)}
                                                        className={styles.editButton}
                                                        title="Edit workspace"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(workspace)}
                                                        className={styles.deleteButton}
                                                        title="Delete workspace"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {(!workspaces || workspaces.length === 0) && (
                                <div className={styles.emptyState}>
                                    <p>No workspaces found. Create your first workspace to get started.</p>
                                </div>
                            )}
                        </div>
                    </div>
                    )}
                </div>
            </div>
            </div>

            {/* Success Toast Notification */}
            <Toast 
                message={successMessage}
                type="success"
                isVisible={showSuccessToast}
                onClose={handleCloseToast}
                duration={8000}
            />
        </>
    )
}